/* eslint-disable max-lines -- one switch per payment method for booking and waitlist; splitting the hook is a refactor outside SQR-15545 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { CartCustomerWithLanguage } from '@/api/booking/upsertCartMutation/types';
import { useToast } from '@/components/_shared/Toaster';
import { storage, storageKeys } from '@/constants/storage';
import { useHasSession } from '@/hooks/auth/useHasSession';
import { useBnplExperimentVariant } from '@/hooks/payments/useBnplExperimentVariant';
import { useDigitalWalletPay } from '@/hooks/payments/useDigitalWalletPay';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  classifyCheckoutError,
  getCheckoutErrorContent,
} from '@/utils/checkoutErrors';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import { isCardRequiredForInPersonPayment } from '@/utils/orderBreakdown';
import type {
  PaymentMethod,
  PaymentMethodCardSaved,
} from '@/utils/paymentMethods';
import {
  PaymentMethodType,
  getTrackingMethod,
  isPaymentMethodBnpl,
} from '@/utils/paymentMethods';
import { Tracker } from '@/utils/tracking';
import {
  startLdCheckout,
  trackLdPaymentResult,
} from '@/utils/tracking/trackLdCheckoutEvents';

import type {
  ChooseTimeScreenBookData,
  UseBookingPaymentOptions,
} from './types';
import { useWaitlistPayment } from './useWaitlistPayment';

export const useBookingPayment = (options: UseBookingPaymentOptions) => {
  const {
    refs,
    cart,
    shop,
    services,
    addPayment,
    addPaymentAndCustomer,
    setLocalCustomer,
    setIsPayInPerson,
    setPayInPersonToken,
    setBnplProviders,
    requestCardForInPersonPayments,
    isWaitlistMode,
    waitlistSelections,
    onWaitlistSuccess,
    waitlistRequiresPayment,
  } = options;

  const { t, i18n } = useScreenTranslations('ChooseTimeScreen');
  const { toast } = useToast();
  const { data: loggedInCustomer } = useCustomerQuery();
  const hasSession = useHasSession();
  const { variant, isEnrolled } = useBnplExperimentVariant(cart, shop);

  // True from the moment "Book" is tapped until either a follow-on sheet takes
  // over (Apple/Google Pay, card form, CashApp, BNPL, consent — each owns its
  // own loading from there) or the action is cancelled/errors. Keeps the
  // underlying Book button in its "Booking…" loading state so the user isn't
  // misled into thinking nothing is happening while a wallet/consent step runs.
  // Held through consent + final checkout; the success path navigates away and
  // unmounts, so it never needs an explicit reset on success.
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // Whether this hook opened the checkout sheet for the current attempt, and
  // whether it then closed it itself. Only the web sheet emits onDismiss for
  // that close (possibly after the next attempt started), so the latch is only
  // ever armed on web and is consumed by that onDismiss, never reset elsewhere.
  const checkoutFormOpenRef = useRef(false);
  const closedCheckoutFormRef = useRef(false);
  // Bumped per Book press; a slow attach PUT from an earlier attempt must not
  // touch the sheet or flag of the attempt that replaced it
  const attemptRef = useRef(0);

  const openCheckoutForm = () => {
    const sheet = refs.checkoutFormBottomSheetRef.current;
    if (!sheet) {
      // Nothing will present or dismiss — don't leave the Book button in flight
      Logger.error('Checkout form sheet is not mounted; cannot open it');
      setIsProcessingPayment(false);
      return;
    }
    checkoutFormOpenRef.current = true;
    sheet.open();
  };

  const {
    makeDigitalWalletPayment: makeApplePayPayment,
    prepareDigitalWalletPayment: prepareApplePayPayment,
    startDigitalWalletPayment: startApplePayPayment,
  } = useDigitalWalletPay('applePay');
  const { makeDigitalWalletPayment: makeGooglePayPayment } =
    useDigitalWalletPay('googlePay');

  const currency = cart?.currency || shop?.brand?.currency || 'USD';
  const shopName = shop?.name ?? '';
  const pendingApplePayRef =
    useRef<ReturnType<typeof startApplePayPayment>>(null);

  useEffect(() => {
    void prepareApplePayPayment({ label: shopName, currency });
  }, [prepareApplePayPayment, shopName, currency]);

  /**
   * Opens the Apple Pay sheet synchronously from the Book tap: on web the
   * browser refuses it after form validation's `await`. `handleBook` then
   * reads the result of this sheet instead of opening another.
   */
  // Opening a sheet consumes the prepared request, so closing one without
  // reading its result must prepare the next, or the next tap opens nothing.
  const closePendingWalletPayment = () => {
    if (!pendingApplePayRef.current) return;
    pendingApplePayRef.current.abort();
    pendingApplePayRef.current = null;
    void prepareApplePayPayment({ label: shopName, currency });
  };

  const beginWalletPayment = (paymentMethod?: PaymentMethod) => {
    if (paymentMethod?.type !== PaymentMethodType.applePay) return;
    closePendingWalletPayment();
    pendingApplePayRef.current = startApplePayPayment(
      cart?.breakdown?.outstandingChargeAmount ?? 0,
      shopName,
      { currency },
    );
  };

  const abortWalletPayment = closePendingWalletPayment;

  const payWithApplePay = async (customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  }) => {
    const pending = pendingApplePayRef.current;
    pendingApplePayRef.current = null;
    if (!pending) {
      return makeApplePayPayment(
        cart?.breakdown?.outstandingChargeAmount ?? 0,
        shopName,
        customer,
        { currency },
      );
    }
    const payment = await pending.result(customer);
    void prepareApplePayPayment({ label: shopName, currency });
    return payment;
  };

  const {
    setPendingWaitlistCustomer,
    isJoiningWaitlist,
    joinAllWaitlistEntries,
    handleSuccessWaitlistPayment,
  } = useWaitlistPayment({
    refs,
    cart,
    shop,
    services,
    waitlistSelections,
    onWaitlistSuccess,
  });

  const openCheckoutFormForWebWallet = () => {
    if (!isWeb()) return false;
    openCheckoutForm();
    return true;
  };

  const handleBook = async (data: ChooseTimeScreenBookData) => {
    const _customer = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      preferredLanguage: i18n.language,
    };

    setLocalCustomer(_customer);
    attemptRef.current += 1;

    // Persist the info the user typed as soon as they tap "Book", before any
    // payment flow runs. This is the one place common to every flow, so flows
    // that leave the app (e.g. BNPL redirect) can pre-fill these fields from
    // guestUserInfo when the user returns.
    if (!hasSession) {
      storage.set(storageKeys.guestUserInfo, JSON.stringify(_customer));
    }

    // Reset pay-in-person state each attempt; only the payInPerson case re-enables it.
    setIsPayInPerson(false);
    setPayInPersonToken(null);

    if (isWaitlistMode) {
      setPendingWaitlistCustomer(_customer);

      Tracker.track('confirm_waitlist', {
        shopId: shop?.id ?? '',
        email: _customer.email,
        firstName: _customer.firstName,
        lastName: _customer.lastName,
      });

      switch (data.paymentMethod.type) {
        case PaymentMethodType.googlePay: {
          if (openCheckoutFormForWebWallet()) break;
          const googlePayPayment = await makeGooglePayPayment(
            cart?.breakdown?.outstandingChargeAmount ?? 0,
            shop?.name ?? '',
            _customer,
            { currency },
          );
          if (!!googlePayPayment.error || !!googlePayPayment.cancelled) {
            toast({
              variant: 'error',
              message:
                googlePayPayment.error?.message ??
                t('error.bookingPaymentError'),
            });
            Logger.error('Google Pay payment failed for waitlist', {
              error: googlePayPayment.error,
              cancelled: googlePayPayment.cancelled,
            });
            return;
          }
          joinAllWaitlistEntries(_customer, {
            paymentToken: googlePayPayment.payload?.paymentToken,
          });
          break;
        }
        case PaymentMethodType.applePay: {
          const applePayPayment = await payWithApplePay(_customer);
          if (applePayPayment.cancelled) {
            return;
          }
          if (applePayPayment.error) {
            toast({
              variant: 'error',
              message:
                applePayPayment.error.message ?? t('error.bookingPaymentError'),
            });
            Logger.error('Apple Pay payment failed for waitlist', {
              error: applePayPayment.error,
            });
            return;
          }
          joinAllWaitlistEntries(_customer, {
            paymentToken: applePayPayment.payload?.paymentToken,
          });
          break;
        }
        case PaymentMethodType.card:
          if (data.paymentMethod.id === 'card') {
            openCheckoutForm();
          } else {
            const savedCard = data.paymentMethod as PaymentMethodCardSaved;
            joinAllWaitlistEntries(_customer, {
              paymentCardId: savedCard.card.id,
            });
          }
          break;
        default:
          if (waitlistRequiresPayment) {
            toast({
              variant: 'error',
              message: t('error.paymentRequiredForWaitlist'),
            });
            return;
          }
          joinAllWaitlistEntries(_customer);
          break;
      }
      return;
    }

    // Fired here, ahead of the per-method switch, so every method is counted
    // exactly once per Book press. Waitlist joins return above and are
    // deliberately excluded: they are not checkouts, and BNPL is never offered
    // in waitlist mode, so those units could never reach the later funnel steps.
    // `method` must be read here, before the per-method switch:
    // handleSuccessBookingPayment rewrites both wallets to
    // PaymentMethodType.card further down, so a later read would label Apple Pay
    // and Google Pay as `card`. `totalDollars` is the full cart total in
    // *dollars*, matching the basis the BNPL path already uses.
    startLdCheckout({
      cartId: cart?.id ?? '',
      shopId: shop?.id ?? '',
      enrolled: isEnrolled,
      variant,
      entryType: loggedInCustomer ? 'authenticated' : 'guest',
      method: getTrackingMethod(data.paymentMethod),
      totalDollars: (cart?.total ?? 0) / 100,
    });

    // Booking is now in flight (non-waitlist). Held until a follow-on sheet
    // takes over or the flow is cancelled/errors (see resets below + the sheet
    // cancel handlers in the screen). Success navigates away and unmounts.
    setIsProcessingPayment(true);

    switch (data.paymentMethod.type) {
      case PaymentMethodType.googlePay: {
        if (openCheckoutFormForWebWallet()) break;
        const googlePayPayment = await makeGooglePayPayment(
          cart?.breakdown?.outstandingChargeAmount ?? 0,
          shop?.name ?? '',
          _customer,
          { currency },
        );
        if (!!googlePayPayment.error || !!googlePayPayment.cancelled) {
          toast({
            variant: 'error',
            message:
              googlePayPayment.error?.message ?? t('error.bookingPaymentError'),
          });
          Logger.error('Google Pay payment failed', {
            error: googlePayPayment.error,
            cancelled: googlePayPayment.cancelled,
          });
          // The sheet collapses both outcomes; the funnel does not. Cancellation
          // is a user choice, an error is a failure — when both are set the
          // user's choice wins.
          trackLdPaymentResult(
            cart?.id ?? '',
            googlePayPayment.cancelled ? 'cancelled' : 'error',
          );
          setIsProcessingPayment(false);
          return;
        }
        handleSuccessBookingPayment(
          {
            id: 'googlePay',
            type: PaymentMethodType.card,
            token: googlePayPayment.payload?.paymentToken ?? '',
          },
          _customer,
        );
        break;
      }
      case PaymentMethodType.applePay: {
        const applePayPayment = await payWithApplePay(_customer);
        if (applePayPayment.cancelled) {
          trackLdPaymentResult(cart?.id ?? '', 'cancelled');
          setIsProcessingPayment(false);
          return;
        }
        if (applePayPayment.error) {
          toast({
            variant: 'error',
            message:
              applePayPayment.error.message ?? t('error.bookingPaymentError'),
          });
          Logger.error('Apple Pay payment failed', {
            error: applePayPayment.error,
          });
          trackLdPaymentResult(cart?.id ?? '', 'error');
          setIsProcessingPayment(false);
          return;
        }

        handleSuccessBookingPayment(
          {
            id: 'applePay',
            type: PaymentMethodType.card,
            token: applePayPayment.payload?.paymentToken ?? '',
          },
          _customer,
        );
        break;
      }
      case PaymentMethodType.card:
        if (data.paymentMethod.id === 'card') {
          openCheckoutForm();
        } else {
          const savedCard = data.paymentMethod as PaymentMethodCardSaved;

          handleSuccessBookingPayment(
            {
              type: PaymentMethodType.card,
              id: savedCard.id,
              card: savedCard.card,
            } as PaymentMethodCardSaved,
            _customer,
          );
        }
        break;
      case PaymentMethodType.cardless:
        if (data.paymentMethod.id === 'cashapp') {
          refs.cashAppBottomSheetRef.current?.open();
        } else if (isPaymentMethodBnpl(data.paymentMethod)) {
          Tracker.track('bnpl_sheet_opened', {
            shopId: shop?.id ?? '',
            cartId: cart?.id ?? '',
          });

          // The sheet offers only the providers of the row the customer
          // picked (SQR-15886). Committed alongside setLocalCustomer below
          // so the deferred open() reads both.
          setBnplProviders(data.paymentMethod.providers);

          // Defer so the setLocalCustomer update above commits before
          // BnplBottomSheet reads its customer prop for Stripe billing details.
          setTimeout(() => refs.bnplBottomSheetRef.current?.open(), 0);
        }
        break;
      case PaymentMethodType.payInPerson:
        setIsPayInPerson(true);

        const cardRequired = isCardRequiredForInPersonPayment({
          requestCardForInPersonPayments,
          canBookNoPay: cart?.breakdown?.canBookNoPay ?? false,
          canReserve: cart?.breakdown?.canReserve ?? false,
        });

        if (cardRequired) {
          openCheckoutForm();
        } else {
          handlePayInPersonWithoutCard(_customer);
        }
        break;
    }
  };

  const handlePayInPersonWithoutCard = async (
    customer: CartCustomerWithLanguage,
  ) => {
    const attempt = attemptRef.current;
    try {
      await addPaymentAndCustomer(null, customer, {
        onSuccess: () => {
          if (attempt !== attemptRef.current) return;
          // Keep the confirm booking sheet open underneath; the consent sheet
          // stacks on top (stackBehavior="push") and checkout proceeds after the
          // user makes a consent choice.
          refs.consentBottomSheetRef.current?.open();
        },
        // The upsert is fire-and-forget, so a rejection never reaches the catch
        onError: () => {
          if (attempt !== attemptRef.current) return;
          setIsProcessingPayment(false);
        },
      });
    } catch (error) {
      Logger.error('Error handling pay in person booking', { error });
      toast({
        variant: 'error',
        message: getCheckoutErrorContent(classifyCheckoutError(error), t)
          .description,
      });
      setIsProcessingPayment(false);
    }
  };

  const handlePayInPersonWithCard = async (
    paymentMethod: PaymentMethod,
    customer?: CartCustomerWithLanguage,
  ) => {
    if (!paymentMethod) return;

    try {
      const token =
        'card' in paymentMethod
          ? paymentMethod.card.id
          : ((paymentMethod as { token?: string }).token ?? null);

      setPayInPersonToken(token);
      const attempt = attemptRef.current;

      const payment =
        'card' in paymentMethod
          ? {
              type: PaymentMethodType.card,
              isApplePay: paymentMethod.card.isApplePay,
              amount: 0,
              paymentCardId: paymentMethod.card.id,
            }
          : {
              type: PaymentMethodType.card,
              paymentToken: (paymentMethod as { token?: string }).token,
              amount: 0,
            };

      const onSuccess = () => {
        if (attempt !== attemptRef.current) return;
        // Keep the confirm booking sheet open underneath; only close the Stripe
        // checkout form. The consent sheet stacks on top and checkout proceeds
        // after the user makes a consent choice.
        closedCheckoutFormRef.current = isWeb() && checkoutFormOpenRef.current;
        refs.checkoutFormBottomSheetRef.current?.close();
        refs.consentBottomSheetRef.current?.open();
      };

      // The upsert is fire-and-forget, so a rejection never reaches the catch
      const onError = () => {
        if (attempt !== attemptRef.current) return;
        setIsProcessingPayment(false);
      };

      if (customer) {
        await addPaymentAndCustomer(payment, customer, { onSuccess, onError });
      } else {
        await addPayment(payment, { onSuccess, onError });
      }
    } catch (error) {
      Logger.error('Error handling pay in person with card', { error });
      toast({
        variant: 'error',
        message: getCheckoutErrorContent(classifyCheckoutError(error), t)
          .description,
      });
      setIsProcessingPayment(false);
    }
  };

  const handleSuccessBookingPayment = useCallback(
    async (
      paymentMethod: PaymentMethod,
      customer?: CartCustomerWithLanguage,
    ) => {
      if (!paymentMethod) return;
      const attempt = attemptRef.current;

      try {
        const payment =
          'card' in paymentMethod
            ? {
                type: PaymentMethodType.card,
                isApplePay:
                  paymentMethod.card.isApplePay ||
                  paymentMethod.id === 'applePay',
                amount: cart?.breakdown?.amountLeftForPayment ?? 0,
                paymentCardId: paymentMethod.card.id,
              }
            : {
                type: PaymentMethodType.card,
                paymentToken: (paymentMethod as { token?: string }).token,
                amount: cart?.breakdown?.amountLeftForPayment ?? 0,
                isApplePay: paymentMethod.id === 'applePay',
              };

        const onSuccess = () => {
          if (attempt !== attemptRef.current) return;
          // Keep the confirm booking sheet open underneath; only close the Stripe
          // checkout form. The consent sheet stacks on top and checkout proceeds
          // after the user makes a consent choice.
          closedCheckoutFormRef.current =
            isWeb() && checkoutFormOpenRef.current;
          refs.checkoutFormBottomSheetRef.current?.close();
          refs.consentBottomSheetRef.current?.open();
        };

        // The upsert is fire-and-forget, so a rejection never reaches the catch
        const onError = () => {
          if (attempt !== attemptRef.current) return;
          setIsProcessingPayment(false);
        };

        if (customer) {
          await addPaymentAndCustomer(payment, customer, {
            onSuccess,
            onError,
          });
        } else {
          await addPayment(payment, { onSuccess, onError });
        }
      } catch (error) {
        Logger.error('Error handling booking payment', { error });
        toast({
          variant: 'error',
          message: getCheckoutErrorContent(classifyCheckoutError(error), t)
            .description,
        });
        setIsProcessingPayment(false);
      }
    },
    [
      addPayment,
      addPaymentAndCustomer,
      cart?.breakdown?.amountLeftForPayment,
      refs,
      t,
      toast,
    ],
  );

  return {
    handleBook,
    beginWalletPayment,
    abortWalletPayment,
    handleSuccessBookingPayment,
    handlePayInPersonWithCard,
    isJoiningWaitlist,
    handleSuccessWaitlistPayment,
    isProcessingPayment,
    resetProcessingPayment: () => setIsProcessingPayment(false),
    handleCheckoutFormDismiss: () => {
      checkoutFormOpenRef.current = false;
      if (closedCheckoutFormRef.current) {
        closedCheckoutFormRef.current = false;
        return;
      }
      setIsProcessingPayment(false);
    },
  };
};
